import {
  useEffect,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import { cn } from "@/lib/utils";

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  hasError?: boolean;
  autoFocus?: boolean;
}

/**
 * 6-digit OTP input. Each digit is its own <input>. Behavior:
 *   - typing a digit advances focus to the next box
 *   - backspace on an empty box moves focus to the previous box
 *   - pasting "123456" distributes digits across all boxes
 *   - non-digit characters are ignored
 */
export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled = false,
  hasError = false,
  autoFocus = true,
}: OtpInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (autoFocus) inputsRef.current[0]?.focus();
  }, [autoFocus]);

  function setAt(idx: number, ch: string) {
    const digits = value.padEnd(length, " ").split("");
    digits[idx] = ch;
    onChange(digits.join("").replace(/\s+$/g, ""));
  }

  function onInput(idx: number, raw: string) {
    const ch = raw.replace(/\D/g, "").slice(-1);
    if (!ch) {
      setAt(idx, "");
      return;
    }
    setAt(idx, ch);
    const next = inputsRef.current[idx + 1];
    if (next) next.focus();
  }

  function onKeyDown(idx: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (value[idx]) {
        setAt(idx, "");
      } else if (idx > 0) {
        inputsRef.current[idx - 1]?.focus();
        setAt(idx - 1, "");
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
      e.preventDefault();
    } else if (e.key === "ArrowRight" && idx < length - 1) {
      inputsRef.current[idx + 1]?.focus();
      e.preventDefault();
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!text) return;
    onChange(text);
    const focusIdx = Math.min(text.length, length - 1);
    inputsRef.current[focusIdx]?.focus();
    e.preventDefault();
  }

  return (
    <div className="flex items-center justify-between gap-2">
      {Array.from({ length }, (_, i) => {
        const ch = value[i] ?? "";
        return (
          <input
            key={i}
            ref={(el) => {
              inputsRef.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={ch}
            onChange={(e) => onInput(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={onPaste}
            disabled={disabled}
            aria-label={`Chữ số thứ ${i + 1}`}
            className={cn(
              "h-12 w-full min-w-0 rounded-md border border-input bg-background text-center text-lg font-semibold text-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              ch && "border-primary",
              hasError && "border-destructive focus-visible:ring-destructive"
            )}
          />
        );
      })}
    </div>
  );
}
